import React, { useEffect, memo, useMemo, useCallback, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import B2BLayout from '../../../layouts/b2b'
import '../index.css'
import { useSelector } from 'react-redux'
import { getHrEnums, getHrForm } from '../../../store/hr/action-creators'
import {
  getCandidateId,
  getHrFormError,
  getHrFormLoading,
  getHrFormData,
  getVisibleFormData,
} from './selectors'
import HrTabs from './ui/hr-tabs/hr-tabs'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { HrFormsHeader } from './ui/hr-forms/hr-form-header/hr-form-header'
import { useHrSaveResultForms } from './hooks/useHrSaveResultForms'
import { Modal, Result } from 'antd'
import { ResultStatusType } from 'antd/lib/result'
import { isFormDataRequired } from '../../../../lib/utils/collections'
import { HrModal } from './ui/hr-modal'
import { HrFormError } from '../../../store/hr/interfaces'

function hrErrorMessage(error: HrFormError): string {
  if (error && typeof error === 'object') {
    return error.message || 'Ошибка'
  }
  return String(error || '')
}

function hrErrorStatus(error: HrFormError): ResultStatusType {
  if (error && typeof error === 'object' && Number(error.statusCode) === 404) {
    return 404
  }
  if (error && typeof error === 'object' && error.statusCode != null) {
    return error.statusCode as ResultStatusType
  }
  return 'error'
}

const HRCandidatePage = memo(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const candidateId = useSelector(getCandidateId)
  const hrFormIsLoading = useSelector(getHrFormLoading)
  const hrFormError = useSelector(getHrFormError)
  const hrFormData = useSelector(getHrFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const entityType = 'hr'
  const { saveResult } = useHrSaveResultForms()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [isDisabled, setIsDisabled] = useState<boolean>()
  const formRef = useRef<typeof visibleFormData>()

  const pageTitle = useMemo(() => (id === 'new' ? 'Новая запись' : `Запись №${id}`), [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/hr',
        title: 'Учет кандидатов',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  useEffect(() => {
    if (visibleFormData) {
      formRef.current = visibleFormData
    }
  }, [visibleFormData])

  useEffect(() => {
    dispatch(getHrEnums())
  }, [dispatch])

  useEffect(() => {
    if (id === 'new' && candidateId) {
      navigate({
        pathname: `/hr/${candidateId}`,
      })
    }
  }, [id, candidateId, navigate])

  useEffect(() => {
    dispatch(getHrForm(id))
  }, [id, dispatch])

  const stageTransitionLockRef = useRef(false)

  const saveCase = useCallback(
    async (isNextStage = false) => {
      if (!formRef.current) return
      if (isNextStage) {
        if (stageTransitionLockRef.current) return
        stageTransitionLockRef.current = true
      }
      try {
        await saveResult(
          id,
          formRef.current,
          hrFormData?.stages?.current,
          isOpenModal,
          setIsOpenModal,
          isNextStage,
          hrFormData?.stages,
        )
      } finally {
        if (isNextStage) {
          stageTransitionLockRef.current = false
        }
      }
    },
    [id, saveResult, hrFormData, isOpenModal],
  )

  const onFormDataModal = useCallback(async ({ formData }) => {
    if (!formRef.current) {
      return
    }
    const isFilesEdit = JSON.stringify(formData) === JSON.stringify(formRef.current?.modal)
    if (isFilesEdit) {
      return
    }
    formRef.current.modal = { ...formData }
  }, [])

  const handleOk = useCallback(async () => {
    if (!formRef.current) {
      return
    }
    const isRequiredModal = isFormDataRequired(
      formRef.current.modal,
      formRef.current.modalFieldsSchema,
    )
    if (!isRequiredModal) {
      Modal.warning({
        title: 'Предупреждение',
        content: 'Заполните все обязательные поля',
      })
      return
    }
    setIsDisabled(true)
    setIsOpenModal(false)
    await saveCase(true)
    setIsDisabled(false)
  }, [saveCase])

  const handleCancel = useCallback(() => {
    setIsOpenModal(false)
  }, [])

  const handleOnClick = useCallback(async () => {
    setIsDisabled(true)
    await saveCase(true)
    setIsDisabled(false)
  }, [saveCase])

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  if (!id) return null

  const isNotFound =
    Boolean(hrFormError) &&
    typeof hrFormError === 'object' &&
    Number(hrFormError.statusCode) === 404

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {hrFormError && !hrFormIsLoading ? (
        <Result
          status={hrErrorStatus(hrFormError)}
          title={isNotFound ? 'Не найдено' : 'Ошибка'}
          subTitle={hrErrorMessage(hrFormError) || (isNotFound ? 'Заявка не найдена' : 'Ошибка')}
        />
      ) : (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <HrFormsHeader
            id={id}
            stage={hrFormData?.stages?.current}
            nextStageOnClick={handleOnClick}
            isDisabled={isDisabled}
            showTitle={false}
          />
          <HrTabs id={id} />
          {visibleFormData && !hrFormIsLoading && visibleFormData?.modal && (
            <HrModal
              schema={visibleFormData.modalFieldsSchema}
              onChange={onFormDataModal}
              formData={visibleFormData.modal}
              isOpen={isOpenModal}
              onCancel={handleCancel}
              onOk={handleOk}
              centered={true}
              disabled={isDisabled}
              title={visibleFormData?.modalTitle}
            />
          )}
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
})

HRCandidatePage.displayName = 'HRCandidatePage'
export default HRCandidatePage
